import dayjs from "dayjs";
export type DateScopeOverrideInput = {
	startDate?: string | null;
	endDate?: string | null;
	comparePreviousPeriod?: boolean | null;
	comparisonStartDate?: string | null;
	comparisonEndDate?: string | null;
};

export type DateScopeContext = {
	selectedStartDate: string;
	selectedEndDate: string;
	compared?: boolean | null;
	comparisonStartDate?: string | null;
	comparisonEndDate?: string | null;
};

export type ResolvedComparisonRange = {
	start: string;
	end: string;
};

export type ResolvedDateScope = {
	startDate: string;
	endDate: string;
	comparison: ResolvedComparisonRange | null;
};

const DATE_YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function normalizeOptionalDate(value: string | null | undefined): string | null {
	if (value === null || value === undefined) {
		return null;
	}

	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : null;
}

function parseDateYmd(value: string, label: string): dayjs.Dayjs {
	if (!DATE_YMD_REGEX.test(value)) {
		throw new Error(`Invalid ${label}. Expected YYYY-MM-DD.`);
	}

	const parsed = dayjs(value);
	if (!parsed.isValid() || parsed.format("YYYY-MM-DD") !== value) {
		throw new Error(`Invalid ${label}. Expected YYYY-MM-DD.`);
	}

	return parsed;
}

function assertRangeOrder(
	start: dayjs.Dayjs,
	end: dayjs.Dayjs,
	label: string,
): void {
	if (start.isAfter(end, "day")) {
		throw new Error(`Invalid ${label}. Start date must be on or before end date.`);
	}
}

function getContextComparisonRange(
	context: DateScopeContext,
): ResolvedComparisonRange | null {
	if (!context.compared) {
		return null;
	}

	const comparisonStartDate = normalizeOptionalDate(context.comparisonStartDate);
	const comparisonEndDate = normalizeOptionalDate(context.comparisonEndDate);
	if (!comparisonStartDate || !comparisonEndDate) {
		return null;
	}

	try {
		const parsedStart = parseDateYmd(comparisonStartDate, "comparisonStartDate");
		const parsedEnd = parseDateYmd(comparisonEndDate, "comparisonEndDate");
		assertRangeOrder(parsedStart, parsedEnd, "comparison date range");
	} catch {
		return null;
	}

	return {
		start: comparisonStartDate,
		end: comparisonEndDate,
	};
}

function getPreviousPeriodRange(
	start: dayjs.Dayjs,
	end: dayjs.Dayjs,
): ResolvedComparisonRange {
	const dayCount = end.diff(start, "day") + 1;
	const previousEnd = start.subtract(1, "day");
	const previousStart = previousEnd.subtract(dayCount - 1, "day");

	return {
		start: previousStart.format("YYYY-MM-DD"),
		end: previousEnd.format("YYYY-MM-DD"),
	};
}

export function resolveDateScope(
	context: DateScopeContext,
	input: DateScopeOverrideInput,
): ResolvedDateScope {
	const startDateOverride = normalizeOptionalDate(input.startDate);
	const endDateOverride = normalizeOptionalDate(input.endDate);

	if (Boolean(startDateOverride) !== Boolean(endDateOverride)) {
		throw new Error("startDate and endDate must be provided together.");
	}

	const startDate = startDateOverride ?? context.selectedStartDate;
	const endDate = endDateOverride ?? context.selectedEndDate;
	const parsedStartDate = parseDateYmd(startDate, "startDate");
	const parsedEndDate = parseDateYmd(endDate, "endDate");
	assertRangeOrder(parsedStartDate, parsedEndDate, "date range");

	const comparisonStartDate = normalizeOptionalDate(input.comparisonStartDate);
	const comparisonEndDate = normalizeOptionalDate(input.comparisonEndDate);
	if (Boolean(comparisonStartDate) !== Boolean(comparisonEndDate)) {
		throw new Error("comparisonStartDate and comparisonEndDate must be provided together.");
	}

	if (comparisonStartDate && comparisonEndDate) {
		const parsedComparisonStart = parseDateYmd(
			comparisonStartDate,
			"comparisonStartDate",
		);
		const parsedComparisonEnd = parseDateYmd(
			comparisonEndDate,
			"comparisonEndDate",
		);
		assertRangeOrder(
			parsedComparisonStart,
			parsedComparisonEnd,
			"comparison date range",
		);

		return {
			startDate,
			endDate,
			comparison: {
				start: comparisonStartDate,
				end: comparisonEndDate,
			},
		};
	}

	if (input.comparePreviousPeriod === true) {
		return {
			startDate,
			endDate,
			comparison: getPreviousPeriodRange(parsedStartDate, parsedEndDate),
		};
	}

	if (input.comparePreviousPeriod === false) {
		return {
			startDate,
			endDate,
			comparison: null,
		};
	}

	return {
		startDate,
		endDate,
		comparison: getContextComparisonRange(context),
	};
}
